import type { LucideIcon } from 'lucide-react';
import {
  Activity, AlertOctagon, AlertTriangle, Archive, Award, BarChart3, Bell, Bot, Brain, Briefcase, Building2, Calculator, ClipboardCheck, ClipboardList, CreditCard, DollarSign, Eye, FileText, Filter, FolderOpen, Gauge, Globe, GraduationCap, Headset, History, Image, Images, Key, Landmark, Lightbulb, Mail, Megaphone, MessageSquare, Monitor, Orbit as OrbitIcon, Phone, PhoneCall, Scale, Settings, Share2, Shield, Smartphone, Sparkles, Star, Ticket, TrendingUp, Trophy, UserCheck, UserCog, UserMinus, Users, UsersRound, Video, Webhook, Workflow, Zap,
} from 'lucide-react';
import { UserRole } from '../types/user.types';
import { isHidden } from './hiddenFeatures';

/**
 * Admin navigation registry — the ONE list of navigation cards for the
 * HQ/admin dashboard (every non-consultant role) and the left sidebar.
 *
 * Moved verbatim out of Dashboard.tsx (2026-10-19): same id, permissionId,
 * title, icon, description, path, color and roles — only grouped into
 * sections. Visibility rules are unchanged (see visibleAdminCards).
 *
 * Adding a card: add it here with a `section`, plus the usual permission
 * registries (permissionConfig, permissionAudit, menuPermissionConfig).
 */

export type AdminSection =
  | 'sales'
  | 'leads'
  | 'cindy'
  | 'bookings'
  | 'callCentre'
  | 'automation'
  | 'marketing'
  | 'team'
  | 'system';

export const ADMIN_SECTIONS: { id: AdminSection; title: string }[] = [
  { id: 'sales', title: 'Sales & Deals' },
  { id: 'leads', title: 'Leads & Contacts' },
  { id: 'cindy', title: 'Cindy & AI' },
  { id: 'bookings', title: 'Bookings & Viewings' },
  { id: 'callCentre', title: 'Call Centre' },
  { id: 'automation', title: 'WhatsApp & Automation' },
  { id: 'marketing', title: 'Marketing & Content' },
  { id: 'team', title: 'Team & Performance' },
  { id: 'system', title: 'Admin & System' },
];

export interface AdminNavCard {
  id: string;
  permissionId: string;
  title: string;
  icon: LucideIcon;
  description: string;
  path: string;
  color: string;
  roles: UserRole[];
  section: AdminSection;
}

export const ADMIN_NAV_CARDS: AdminNavCard[] = [
  // ── Sales & Deals ──
  {
    id: 'propcloud-hq',
    permissionId: 'dashboardCards.hq',  // Changed to match hierarchical permission format
    title: 'HQ',
    icon: Building2,
    description: 'Manage developments, units, and deals',
    path: '/propcloud-hq',
    color: 'from-green-400 to-green-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.HQ_CALL_CENTRE_AGENT
    ],
    section: 'sales',
  },
  {
    id: 'propcloud-partner',
    permissionId: 'dashboardCards.partner',
    title: 'Partner',
    icon: Users,
    description: 'Partner portal and collaboration',
    path: '/propcloud-partner',
    color: 'from-blue-400 to-blue-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.INDEPENDENT_PARTNER_LVL3
    ],
    section: 'sales',
  },
  {
    // The v2 CRM is now the primary CRM for all consultants. The legacy CRM
    // remains reachable at /crm as an escape hatch but has no dashboard card.
    id: 'crm',
    permissionId: 'dashboardCards.crm',
    title: 'CRM',
    icon: UserCheck,
    description: 'Manage contacts and deals',
    path: '/crm-v2',
    color: 'from-purple-400 to-purple-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ],
    section: 'sales',
  },
  {
    id: 'investmentProposals',
    permissionId: 'dashboardCards.investmentProposals',
    title: 'Investment Proposals',
    icon: FileText,
    description: 'Build multi-property projections and download as branded PDFs',
    path: '/proposals',
    color: 'from-green-400 to-green-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2
    ],
    section: 'sales',
  },
  {
    id: 'allProposals',
    permissionId: 'dashboardCards.allProposals',
    title: 'All Investment Proposals',
    icon: FileText,
    description: 'Company-wide view of every proposal generated by every consultant',
    path: '/proposals/all',
    color: 'from-emerald-500 to-emerald-700',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'sales',
  },
  {
    id: 'capital-investment',
    permissionId: 'dashboardCards.capital_investment',
    title: 'Capital Investment',
    icon: Landmark,
    description: 'Capital investment leads (incl. 2nd-owner contacts) and insights',
    path: '/capital-investment',
    color: 'from-green-500 to-emerald-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2],
    section: 'sales',
  },
  {
    id: 'active-sales-pipeline',
    permissionId: 'dashboardCards.activeSalesPipeline',
    title: 'Active Sales Pipeline',
    icon: Activity,
    description: 'Every in-progress deal — contact, development, consultant, source & CC-qualified agent',
    path: '/admin/active-sales-pipeline',
    color: 'from-blue-400 to-indigo-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'sales',
  },
  {
    id: 'expired-reservations',
    permissionId: 'dashboardCards.expired_reservations',
    title: 'Expired Reservations',
    icon: History,
    description: 'Restore expired reservations from their 14-day snapshots',
    path: '/expired-reservations',
    color: 'from-amber-400 to-amber-600',
    roles: [UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'sales',
  },
  {
    id: 'old-deals',
    permissionId: 'dashboardCards.old_deals',
    title: 'Old Deals',
    icon: Archive,
    description: 'Import and manage historical deal records',
    path: '/admin/legacy-deals',
    color: 'from-amber-400 to-amber-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'sales',
  },
  {
    id: 'accounting',
    permissionId: 'dashboardCards.accounting',
    title: 'Invoices',
    icon: Calculator,
    description: 'Manage invoices, pro-formas, and payments',
    path: '/accounting',
    color: 'from-emerald-400 to-emerald-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'sales',
  },
  {
    id: 'deposit-waiver-report',
    permissionId: 'dashboardCards.deposit_waiver_report',
    title: 'Deposit Waiver Report',
    icon: Scale,
    description: 'Deals with deposit waivers or money at an attorney — excluded from sales stats',
    path: '/deposit-waiver-report',
    color: 'from-amber-400 to-orange-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'sales',
  },
  {
    id: 'credit-scores',
    permissionId: 'dashboardCards.credit_scores',
    title: 'Credit Scores',
    icon: CreditCard,
    description: 'View credit checks and pre-qualifications',
    path: '/admin/credit-score-submissions',
    color: 'from-violet-400 to-violet-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
    ],
    section: 'sales',
  },
  {
    id: 'reports',
    permissionId: 'dashboardCards.reports',
    title: 'Reports',
    icon: FileText,
    description: 'View and export system reports',
    path: '/reports',
    color: 'from-teal-400 to-teal-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'sales',
  },
  // ── Leads & Contacts ──
  {
    id: 'all-contacts',
    permissionId: 'dashboardCards.all_contacts',
    title: 'All Contacts',
    icon: UsersRound,
    description: 'The full contact database — search, filter and export every contact by source, consultant, date and more',
    path: '/admin/all-contacts',
    color: 'from-violet-400 to-violet-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'leads',
  },
  {
    id: 'vip-clients',
    permissionId: 'dashboardCards.vip_clients',
    title: 'VIP Clients',
    icon: Trophy,
    description: 'Complete sales, CC qualified, mobile app, website & WhatsApp leads',
    path: '/admin/vip-clients',
    color: 'from-amber-400 to-amber-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
    section: 'leads',
  },
  {
    id: 'buyers-list',
    permissionId: 'dashboardCards.buyers_list',
    title: 'Buyers List',
    icon: Users,
    description: 'All buyers who purchased a development, with demographics and export',
    path: '/admin/buyers-list',
    color: 'from-emerald-400 to-emerald-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
    ],
    section: 'leads',
  },
  {
    id: 'custom-audiences',
    permissionId: 'dashboardCards.custom_audiences',
    title: 'Custom Audiences',
    icon: Filter,
    description: 'View and edit saved audiences for WhatsApp & marketing campaigns',
    path: '/admin/custom-audiences',
    color: 'from-cyan-400 to-blue-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
    ],
    section: 'leads',
  },
  {
    id: 'lead-stats',
    permissionId: 'dashboardCards.leadStats',
    title: 'Lead Stats',
    icon: TrendingUp,
    description: 'View lead generation analytics and insights',
    path: '/propcloud/crm/lead-stats',
    color: 'from-indigo-400 to-indigo-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
      // NOTE: Consultants (L1/L2) should NOT see Lead Stats - they use CRM for their own leads
    ],
    section: 'leads',
  },
  {
    id: 'lead-intelligence',
    permissionId: 'dashboardCards.leadIntelligence',
    title: 'Lead Intelligence',
    icon: BarChart3,
    description: 'Marketing ROI, source performance & conversion funnel',
    path: '/admin/lead-intelligence',
    color: 'from-green-400 to-emerald-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
    ],
    section: 'leads',
  },
  {
    id: 'statistics',
    permissionId: 'dashboardCards.statistics',
    title: 'Statistics',
    icon: TrendingUp,
    description: 'View system statistics and analytics',
    path: '/stats',
    color: 'from-indigo-400 to-indigo-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER
    ],
    section: 'leads',
  },
  {
    id: 'lead-assignment-history',
    permissionId: 'dashboardCards.lead_assignment_history',
    title: 'Lead Assignment History',
    icon: History,
    description: 'Every lead owner change: round robin, inactive reassigns, vacation cover, Capacity Cover moves',
    path: '/admin/lead-assignment-history',
    color: 'from-amber-500 to-amber-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'leads',
  },
  {
    id: 'lead-distribution',
    permissionId: 'dashboardCards.lead_distribution',
    title: 'Lead Distribution',
    icon: Scale,
    description: 'Performance-weighted lead sharing: tiers, weights, on/off switch, live fair share and allocation log',
    path: '/admin/lead-distribution',
    color: 'from-emerald-500 to-emerald-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'leads',
  },
  {
    id: 'lead-assignment-audit',
    permissionId: 'dashboardCards.lead_assignment_audit',
    title: 'Lead Assignment Audit',
    icon: Activity,
    description: 'Per-contact ownership history — investigate disputes, trace reassignments',
    path: '/lead-assignment-audit',
    color: 'from-cyan-400 to-cyan-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'leads',
  },
  {
    id: 'consultant-transfer',
    permissionId: 'dashboardCards.consultant_transfer',
    title: 'Consultant Transfer',
    icon: UserMinus,
    description: 'Transfer contacts / leads to another consultant when one leaves',
    path: '/consultant-transfer',
    color: 'from-amber-400 to-amber-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'leads',
  },
  {
    id: 'consultant-lead-report',
    permissionId: 'dashboardCards.consultant_lead_report',
    title: 'Consultant Lead Report',
    icon: ClipboardList,
    description: 'Per-consultant engagement events over a date range — exportable to Excel',
    path: '/consultant-lead-report',
    color: 'from-indigo-400 to-indigo-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'leads',
  },
  // ── Cindy & AI ──
  {
    id: 'my-cindy-orbit',
    permissionId: 'dashboardCards.my_cindy_orbit',
    title: 'My Cindy Orbit',
    icon: OrbitIcon,
    description: 'Live orbit of your own Cindy-managed leads',
    path: '/my-cindy-orbit',
    color: 'from-emerald-400 to-teal-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2
    ],
    section: 'cindy',
  },
  {
    id: 'escalations',
    permissionId: 'dashboardCards.escalations',
    title: 'Escalations',
    icon: AlertTriangle,
    description: 'Leads the AI assistant handed to a consultant',
    path: '/escalations',
    color: 'from-amber-400 to-red-500',
    roles: [UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'cindy-leads',
    permissionId: 'dashboardCards.cindy_leads',
    title: 'Cindy Leads',
    icon: Zap,
    description: 'New Leads Management, Old Lead Warm-up, source switch-over and safety',
    path: '/admin/cindy-leads',
    color: 'from-amber-400 to-amber-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'aria-mission-control',
    permissionId: 'dashboardCards.aria_mission_control',
    title: 'Cindy Mission Control',
    icon: Bot,
    description: 'AI consultant live view: funnel, activity feed, replay',
    path: '/admin/aria-mission-control',
    color: 'from-slate-500 to-slate-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'cindy',
  },
  {
    id: 'management-scoreboard',
    permissionId: 'dashboardCards.management_scoreboard',
    title: 'Management Scoreboard',
    icon: BarChart3,
    description: 'Deal strip, Cindy / Consultants / Call centre boards, Intent Review',
    path: '/admin/scoreboard/cindy',
    color: 'from-[#0b1f33] to-[#1f3a57]',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'cindy-analytics',
    permissionId: 'dashboardCards.cindy_analytics',
    title: 'Cindy Analytics',
    icon: BarChart3,
    description: 'Cindy performance: enrolments, replies, bookings, escalations — New vs Warm-up',
    path: '/admin/cindy-analytics',
    color: 'from-sky-400 to-sky-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'cindy',
  },
  {
    id: 'cindy-cost-monitor',
    permissionId: 'dashboardCards.cindy_cost_monitor',
    title: 'Cindy Cost Monitor',
    icon: DollarSign,
    description: 'LLM spend, cache performance, savings vs baseline and cost projections',
    path: '/admin/cindy-cost-monitor',
    color: 'from-emerald-500 to-emerald-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'cindy',
  },
  {
    id: 'aria-training-console',
    permissionId: 'dashboardCards.aria_training_console',
    title: 'Cindy Training Console',
    icon: Sparkles,
    description: 'Curate the AI consultant: knowledge, documents, persona, settings',
    path: '/admin/aria-training',
    color: 'from-violet-400 to-violet-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'heidi-console',
    permissionId: 'dashboardCards.heidi_console',
    title: 'Heidi Console',
    icon: UserCheck,
    description: 'Bonds AI assistant (secret until launch): settings and the required-documents list',
    path: '/admin/heidi',
    color: 'from-rose-400 to-rose-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'wendy-console',
    permissionId: 'dashboardCards.wendy_console',
    title: 'Wendy Console',
    icon: Headset,
    description: 'AI call-centre agent (invisible until launch): settings, test chat, conversations',
    path: '/admin/wendy',
    color: 'from-teal-400 to-teal-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'voice-lab',
    permissionId: 'dashboardCards.voice_lab',
    title: 'Cindy Voice Lab',
    icon: Phone,
    description: 'Test bench: talk to Cindy by voice and compare engines. Invisible to leads',
    path: '/admin/voice-lab',
    color: 'from-indigo-500 to-indigo-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'cindy-test-form',
    permissionId: 'dashboardCards.cindy_test_form',
    title: 'Cindy Test Form',
    icon: Bot,
    description: 'Submit yourself as a lead and watch the full Cindy workflow run',
    path: '/admin/cindy-test-form',
    color: 'from-purple-500 to-purple-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'ai-assistant',
    permissionId: 'dashboardCards.ai_assistant',
    title: 'AI Assistant',
    icon: Brain,
    description: 'Configure AI assistant settings and knowledge base',
    path: '/admin/ai-assistant',
    color: 'from-purple-500 to-purple-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  {
    id: 'ai-training',
    permissionId: 'dashboardCards.ai_training',
    title: 'AI Training',
    icon: Sparkles,
    description: 'Configure AI email generation prompts, terminology, and training',
    path: '/admin/ai-training',
    color: 'from-violet-400 to-violet-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'cindy',
  },
  // ── Bookings & Viewings ──
  {
    id: 'consultations',
    permissionId: 'dashboardCards.consultations',
    title: 'Consultations',
    icon: Video,
    description: 'Schedule meetings to discuss costs and bond repayments',
    path: '/consultations',
    color: 'from-cyan-400 to-cyan-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ],
    section: 'bookings',
  },
  {
    id: 'booking-outcomes',
    permissionId: 'dashboardCards.booking_outcomes',
    title: 'Booking Outcomes',
    icon: ClipboardCheck,
    description: 'Record what happened with each consultation (compulsory)',
    path: '/booking-outcomes',
    color: 'from-emerald-400 to-green-600',
    roles: [UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'bookings',
  },
  {
    id: 'viewing',
    permissionId: 'dashboardCards.viewing',
    title: 'Viewing Management',
    icon: Activity,
    description: 'Manage property viewings and reports',
    path: '/viewing',
    color: 'from-teal-400 to-teal-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN
    ],
    section: 'bookings',
  },
  {
    id: 'viewing-oversight',
    permissionId: 'dashboardCards.viewingOversight',
    title: 'Viewing Oversight',
    icon: Eye,
    description: 'Complete oversight of all property viewings',
    path: '/viewing-oversight',
    color: 'from-amber-400 to-amber-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_VIEWING_COORDINATOR
    ],
    section: 'bookings',
  },
  {
    id: 'viewing-settings',
    permissionId: 'dashboardCards.viewing_settings',
    title: 'Viewing Settings',
    icon: Eye,
    description: 'Configure viewing agent webhooks and defaults',
    path: '/viewing-settings',
    color: 'from-emerald-400 to-emerald-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'bookings',
  },
  {
    id: 'viewing-webhook-testing',
    permissionId: 'dashboardCards.viewing_webhook_testing',
    title: 'Viewing Webhook Testing',
    icon: Webhook,
    description: 'Test viewing webhooks for GoHighLevel integration',
    path: '/admin/viewing-webhooks',
    color: 'from-indigo-400 to-indigo-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'bookings',
  },
  // ── Call Centre ──
  {
    id: 'call-centre-overview',
    permissionId: 'dashboardCards.call_centre_overview',
    title: 'Call Centre Overview',
    icon: Headset,
    description: 'Agent performance stats, leaderboard & disposition breakdown',
    path: '/admin/call-centre-overview',
    color: 'from-teal-400 to-teal-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'callCentre',
  },
  {
    id: 'disposition-reconciliation',
    permissionId: 'dashboardCards.disposition_reconciliation',
    title: 'Disposition Reconciliation',
    icon: Activity,
    description: 'Audit and reconcile Euphoria dispositions — protects agent incentive accuracy',
    path: '/disposition-reconciliation',
    color: 'from-blue-400 to-blue-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'callCentre',
  },
  {
    id: 'call-centre-config',
    permissionId: 'dashboardCards.call_centre_config',
    title: 'Call Centre Config',
    icon: Phone,
    description: 'Configure auto-push to Euphoria dialer for incoming leads',
    path: '/admin/call-centre-config',
    color: 'from-teal-400 to-teal-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER], // Fallback only - use Roles & Permissions to grant access,
    section: 'callCentre',
  },
  {
    id: 'dialer-simulation',
    permissionId: 'dashboardCards.dialer_simulation',
    title: 'Dialer Test',
    icon: PhoneCall,
    description: 'Push simulated leads to the Euphoria dialer for testing',
    path: '/admin/dialer-simulation',
    color: 'from-orange-400 to-orange-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'callCentre',
  },
  {
    id: 'call-centre-bonus-audit',
    permissionId: 'dashboardCards.call_centre_bonus_audit',
    title: 'Call Centre Bonus Audit',
    icon: Headset,
    description: 'Per 100% complete: who made first contact, call centre or consultant — with the evidence',
    path: '/call-centre-bonus-audit',
    color: 'from-emerald-400 to-emerald-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'callCentre',
  },
  // ── WhatsApp & Automation ──
  {
    id: 'whatsapp-automation-center',
    permissionId: 'dashboardCards.whatsapp_automation_center',
    title: 'WhatsApp Automation Center',
    icon: Zap,
    description: 'Toggles and templates for all automated outbound WhatsApp messages',
    path: '/admin/whatsapp-automation-center',
    color: 'from-emerald-400 to-green-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'automation',
  },
  {
    id: 'whatsapp-bot-builder',
    permissionId: 'dashboardCards.whatsapp_bot_builder',
    title: 'WhatsApp Bot Builder',
    icon: Workflow,
    description: 'Visual drag-and-drop builder for automated WhatsApp reply flows and menus',
    path: '/admin/whatsapp-bot-builder',
    color: 'from-emerald-400 to-green-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'automation',
  },
  {
    id: 'whatsapp-bot-analytics',
    permissionId: 'dashboardCards.whatsapp_bot_analytics',
    title: 'WhatsApp Bot Analytics',
    icon: BarChart3,
    description: 'Conversation volume, flow performance, drop-off funnels, attribution and health for the published WhatsApp bot',
    path: '/whatsapp/bot-analytics',
    color: 'from-emerald-500 to-teal-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'automation',
  },
  {
    id: 'whatsapp-followups-config',
    permissionId: 'dashboardCards.whatsapp_followups_config',
    title: 'WhatsApp Follow-Ups',
    icon: MessageSquare,
    description: 'Configure scheduled WhatsApp follow-ups (T+2 thank-you, T+14 review request)',
    path: '/admin/whatsapp-follow-ups',
    color: 'from-green-400 to-green-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'automation',
  },
  {
    id: 'lead-automation',
    permissionId: 'dashboardCards.lead_automation',
    title: 'Lead Automation',
    icon: Zap,
    description: 'Configure automated lead follow-up pipeline and notifications',
    path: '/admin/lead-automation',
    color: 'from-yellow-400 to-yellow-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'automation',
  },
  {
    id: 'automation-logs',
    permissionId: 'dashboardCards.lead_automation',
    title: 'Automation Logs',
    icon: Activity,
    description: 'Monitor lead automation workflows and troubleshoot issues',
    path: '/admin/automation-logs',
    color: 'from-cyan-400 to-cyan-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'automation',
  },
  {
    id: 'notifications-admin',
    permissionId: 'dashboardCards.notifications_admin',
    title: 'Notifications',
    icon: Bell,
    description: 'Every in-app notification the system has sent, across all users',
    path: '/admin/notifications',
    color: 'from-sky-500 to-sky-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'automation',
  },
  {
    id: 'notification-settings',
    permissionId: 'dashboardCards.notification_settings',
    title: 'Notification Settings',
    icon: Bell,
    description: 'Configure development transfer date notifications',
    path: '/notification-settings',
    color: 'from-sky-400 to-sky-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'automation',
  },
  {
    // Email reports framework (2026-10-26): recipients, Rand-figure flags,
    // schedules and test mode for the scheduled reports. Super admins only.
    id: 'email-reports',
    permissionId: 'dashboardCards.email_reports',
    title: 'Email Reports',
    icon: Mail,
    description: 'Recipients, schedules, test mode and run log for the scheduled email reports',
    path: '/admin/email-reports',
    color: 'from-sky-400 to-indigo-500',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'automation',
  },
  // ── Marketing & Content ──
  {
    id: 'advertising',
    permissionId: 'dashboardCards.advertising',
    title: 'Advertising',
    icon: Megaphone,
    description: 'Marketing campaigns and ads',
    path: '/advertising',
    color: 'from-pink-400 to-pink-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_ADVERTISING
      // NOTE: HQ_ADMIN and HQ_MANAGER should not see Advertising
    ],
    section: 'marketing',
  },
  {
    id: 'graphic-design',
    permissionId: 'dashboardCards.graphicDesign',
    title: 'Graphic Design',
    icon: Image,
    description: 'Manage development images and brochures',
    path: '/graphic-design',
    color: 'from-purple-400 to-purple-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_ADVERTISING
    ],
    section: 'marketing',
  },
  {
    id: 'media-manager',
    permissionId: 'dashboardCards.media_manager',
    title: 'Media Manager',
    icon: Images,
    description: 'Upload and manage development photos and videos for marketing',
    path: '/admin/media-manager',
    color: 'from-cyan-400 to-cyan-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'marketing',
  },
  {
    id: 'website-cms',
    permissionId: 'dashboardCards.website_cms',
    title: 'Website CMS',
    icon: Globe,
    description: 'Manage public website content, videos, images, and settings',
    path: '/admin/website-cms',
    color: 'from-emerald-400 to-emerald-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'marketing',
  },
  {
    id: 'public-website-manager',
    permissionId: 'dashboardCards.public_website_manager',
    title: 'Public Website Manager',
    icon: Globe,
    description: 'Manage which properties appear on the Invest SA public website',
    path: '/admin/public-website',
    color: 'from-green-400 to-green-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'marketing',
  },
  {
    id: 'mobile-app-cms',
    permissionId: 'dashboardCards.mobile_app_cms',
    title: 'Mobile App CMS',
    icon: Smartphone,
    description: 'Manage PropInvest mobile app content, users, and push notifications',
    path: '/admin/mobile-app-cms',
    color: 'from-violet-400 to-violet-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'marketing',
  },
  {
    id: 'syndication-config',
    permissionId: 'dashboardCards.syndication_config',
    title: 'Syndication',
    icon: Share2,
    description: 'Configure PropData syndication to property portals',
    path: '/admin/syndication',
    color: 'from-blue-400 to-blue-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'marketing',
  },
  {
    id: 'reviews-admin',
    permissionId: 'dashboardCards.reviews_admin',
    title: 'Customer Reviews',
    icon: Star,
    description: 'View customer reviews and per-consultant rating insights, including NPS',
    path: '/admin/reviews',
    color: 'from-yellow-400 to-amber-500',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'marketing',
  },
  {
    id: 'careers-applications',
    permissionId: 'dashboardCards.careers_applications',
    title: 'Careers',
    icon: Briefcase,
    description: 'Website job applications by position, AI qualification rules and manual verdicts',
    path: '/careers-applications',
    color: 'from-[#6eb041] to-[#4e8a2c]',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'marketing',
  },
  // ── Team & Performance ──
  {
    id: 'hq-management',
    permissionId: 'dashboardCards.hq_management',
    title: 'HQ Management',
    icon: Building2,
    description: 'HQ organizational setup, agents, leads, and marketing',
    path: '/hq-management',
    color: 'from-green-400 to-green-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
      // NOTE: HQ Management is a super admin tool only
      // HQ_ADMIN, HQ_MANAGER, HQ_BONDS should use other modules
    ],
    section: 'team',
  },
  {
    id: 'consultant-reviews',
    permissionId: 'dashboardCards.consultant_reviews',
    title: 'Consultant Reviews',
    icon: ClipboardCheck,
    description: 'Monthly lead-quality & ethics reviews — manage suspensions and audit conflicts',
    path: '/consultant-reviews',
    color: 'from-emerald-400 to-emerald-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'team',
  },
  {
    id: 'bonus-audit',
    permissionId: 'dashboardCards.bonus_audit',
    title: 'Bonus Audit',
    icon: Award,
    description: 'Monitor and verify consultant monthly & quarterly bonus qualifications',
    path: '/bonus-audit',
    color: 'from-green-400 to-green-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'team',
  },
  {
    id: 'outstanding-bonds',
    permissionId: 'dashboardCards.outstanding_bonds',
    title: 'Outstanding Bonds',
    icon: Landmark,
    description: 'PropInvest Bonds deals older than 2 weeks: banks, timers, notes and AI insights',
    path: '/admin/outstanding-bonds',
    color: 'from-sky-500 to-blue-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN],
    section: 'team',
  },
  {
    id: 'bonds-bonus-admin',
    permissionId: 'dashboardCards.bonds_bonus_admin',
    title: 'Bonds Bonus Admin',
    icon: Calculator,
    description: 'Audit bond bonuses, apply overrides, mark payments',
    path: '/bonds-bonus-admin',
    color: 'from-indigo-400 to-indigo-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'team',
  },
  {
    id: 'bonds-bonus-dashboard',
    permissionId: 'dashboardCards.bonds_bonus_dashboard',
    title: 'Bonds Bonus Dashboard',
    icon: TrendingUp,
    description: 'Your monthly bond bonus performance — tier progress & qualifying deals',
    path: '/bonds-bonus',
    color: 'from-emerald-400 to-emerald-600',
    roles: [UserRole.HQ_BONDS],
    section: 'team',
  },
  {
    // Everyone (Jaco 2026-10-26): the weekly sales-meeting page; every role
    // sees every consultant's meetings. hasPermission always grants it.
    id: 'consultation-insights',
    permissionId: 'dashboardCards.consultation_insights',
    title: 'Consultation Insights',
    icon: Lightbulb,
    description: "Last week's meetings, outcomes, notes and AI bottlenecks for every consultant",
    path: '/consultation-insights',
    color: 'from-amber-400 to-orange-500',
    roles: Object.values(UserRole),
    section: 'team',
  },
  {
    id: 'performance-hub',
    permissionId: 'dashboardCards.performance_hub',
    title: 'Performance Hub',
    icon: Gauge,
    description: 'Your deal pipeline, commissions, payments and loans at a glance',
    path: '/consultant/performance-hub',
    color: 'from-sky-400 to-sky-600',
    roles: [UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2],
    section: 'team',
  },
  {
    id: 'my-monthly-review',
    permissionId: 'dashboardCards.my_monthly_review',
    title: 'My Monthly Review',
    icon: ClipboardCheck,
    description: 'Complete your monthly lead-quality & ethics review',
    path: '/consultant/monthly-review',
    color: 'from-green-400 to-green-600',
    roles: [UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2],
    section: 'team',
  },
  {
    id: 'training',
    permissionId: 'dashboardCards.training',
    title: 'Training',
    icon: GraduationCap,
    description: 'Watch training videos and tutorials',
    path: '/training',
    color: 'from-orange-400 to-orange-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ],
    section: 'team',
  },
  {
    id: 'training-management',
    permissionId: 'dashboardCards.consultant_quiz_results',
    title: 'Training Management',
    icon: Monitor,
    description: 'Upload training videos, track consultant progress and quiz results',
    path: '/admin/training-management',
    color: 'from-indigo-400 to-indigo-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'team',
  },
  {
    id: 'my-documents',
    permissionId: 'dashboardCards.my_documents',
    title: 'My Documents',
    icon: FolderOpen,
    description: 'View and manage your signed documents',
    path: '/my-documents',
    color: 'from-cyan-400 to-cyan-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ],
    section: 'team',
  },
  // ── Admin & System ──
  {
    id: 'user-management',
    permissionId: 'dashboardCards.user_management',
    title: 'User Management',
    icon: Users,
    description: 'Manage users and accounts',
    path: '/users',
    color: 'from-emerald-400 to-emerald-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.HQ_MANAGER
    ],
    section: 'system',
  },
  {
    id: 'roles-permissions',
    permissionId: 'dashboardCards.roles_permissions',
    title: 'Roles & Permissions',
    icon: Shield,
    description: 'Manage user roles and permissions',
    path: '/roles-permissions', 
    color: 'from-red-400 to-red-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'user-impersonation',
    permissionId: 'dashboardCards.user_impersonation',
    title: 'User Impersonation',
    icon: UserCog,
    description: 'Test functionality as different user roles',
    path: '/admin/user-impersonation',
    color: 'from-violet-500 to-violet-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
    section: 'system',
  },
  {
    id: 'general-settings',
    permissionId: 'dashboardCards.general_settings',
    title: 'General Settings',
    icon: Settings,
    description: 'Configure commissions, VAT, and general settings',
    path: '/commission-settings',
    color: 'from-amber-400 to-amber-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'system-settings',
    permissionId: 'dashboardCards.system_settings',
    title: 'System Settings',
    icon: Settings,
    description: 'Configure system settings and branding',
    path: '/system-settings',
    color: 'from-gray-400 to-gray-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'partner-settings',
    permissionId: 'dashboardCards.partner_settings',
    title: 'Partner Settings',
    icon: Building2,
    description: 'Manage partner settings and integrations (SendGrid, Meta, SMS)',
    path: '/partner-settings',
    color: 'from-blue-400 to-blue-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'secrets-management',
    permissionId: 'dashboardCards.secrets_management',
    title: 'Secrets',
    icon: Key,
    description: 'Rotate API keys and server-side secrets (Google Secret Manager)',
    path: '/admin/secrets',
    color: 'from-amber-400 to-orange-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'system-cleanup',
    permissionId: 'dashboardCards.system_cleanup',
    title: 'System Cleanup',
    icon: AlertOctagon,
    description: 'Clean up system data and orphaned records',
    path: '/admin/system-cleanup',
    color: 'from-red-500 to-red-700',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'activities',
    permissionId: 'dashboardCards.activities',
    title: 'Activities',
    icon: Activity,
    description: 'View HQ and Partner activity logs',
    path: '/activities',
    color: 'from-purple-400 to-purple-600',
    roles: [UserRole.PROPCLOUDAI_SUPER_USER],
    section: 'system',
  },
  {
    id: 'ticket-management',
    permissionId: 'dashboardCards.ticket_management',
    title: 'Support Hub',
    icon: Ticket,
    description: 'One queue for tickets, feedback and screen notes',
    path: '/admin/support-hub',
    color: 'from-cyan-400 to-cyan-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER
    ],
    section: 'system',
  },
  {
    // Report an Issue — temporary card for the 2026 consultant CRM audit window.
    // Submissions are saved to the `consultantFeedback` collection AND
    // emailed to jacos@propinvest.co.za via a Cloud Function trigger. See
    // docs/audit/consultant-crm-audit-plan.md for the audit context.
    id: 'feedback',
    permissionId: 'dashboardCards.feedback',
    title: 'Report an Issue',
    icon: MessageSquare,
    description: 'Tell us about a bug, missing feature, or confusing UX',
    path: '/feedback',
    color: 'from-orange-400 to-orange-600',
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.HQ_CALL_CENTRE_AGENT,
      UserRole.HQ_VIEWING_COORDINATOR,
      UserRole.HQ_ADVERTISING,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3,
      UserRole.VIEWING_AGENT,
    ],
    section: 'system',
  },
];

// ---------------------------------------------------------------------------
// Visibility — exactly the rules Dashboard.tsx applied before the move.
// ---------------------------------------------------------------------------

export interface AdminCardAccess {
  role: UserRole | undefined;
  hasHierarchicalPermissions: boolean;
  hasPermission: (permissionId: string) => boolean;
  canAccessProposals: boolean;
  canAccessCapital: boolean;
}

export function visibleAdminCards(cards: AdminNavCard[], access: AdminCardAccess): AdminNavCard[] {
  return cards.filter((card) => {
    // Beta gates — override role + permission.
    if ((card.id === 'investmentProposals' || card.id === 'allProposals') && !access.canAccessProposals) return false;
    if (card.id === 'capital-investment' && !access.canAccessCapital) return false;
    // Placeholder pages hidden via src/config/hiddenFeatures.ts
    if (card.id === 'reports' && isHidden('dashboardReportsCard')) return false;
    if (card.id === 'statistics' && isHidden('dashboardStatisticsCard')) return false;
    if (access.hasHierarchicalPermissions) return access.hasPermission(card.permissionId);
    // Fallback to role-based access if hierarchical permissions aren't configured
    return card.roles.includes(access.role || UserRole.PARTNER_AGENT_LVL3);
  });
}

/** Roles that get the HQ/admin dashboard + sidebar (everyone except the roles with their own home). */
export function usesAdminDashboard(role: UserRole | undefined): boolean {
  if (!role) return false;
  return ![
    UserRole.HQ_CONSULTANT_LVL1,
    UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    UserRole.AI_AGENT_LVL1,
    UserRole.VIEWING_AGENT,
    UserRole.HQ_CALL_CENTRE_AGENT,
  ].includes(role);
}

/** The card a route belongs to — longest matching path prefix wins. */
export function cardForPath(pathname: string, cards: AdminNavCard[]): AdminNavCard | null {
  let best: AdminNavCard | null = null;
  for (const card of cards) {
    const p = card.path.replace(/\/+$/, '');
    if (!p) continue;
    if ((pathname === p || pathname.startsWith(`${p}/`)) && (!best || p.length > best.path.length)) best = card;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Usage — per-user, stored at users/{id}.preferences.adminNav
// ---------------------------------------------------------------------------

export const DAY_MS = 24 * 60 * 60 * 1000;
/** Cards not opened for this long move to "Rarely Used" (Jaco 2026-10-19: 30 days). */
export const RARELY_USED_AFTER_DAYS = 30;
/** Window for "most used". */
export const MOST_USED_WINDOW_DAYS = 30;

export interface AdminCardUsage {
  /** ms epoch of the last open. */
  last: number;
  /** Distinct days (ms epoch / DAY_MS, floored) the card was opened in the last MOST_USED_WINDOW_DAYS. */
  days: number[];
}

export interface AdminNavPrefs {
  /** First time usage tracking ran for this user — nothing is "rarely used" before +RARELY_USED_AFTER_DAYS. */
  trackingStartedAt?: number;
  sidebarCollapsed?: boolean;
  pinned?: string[];
  usage?: Record<string, AdminCardUsage>;
  updatedAt?: number;
}

/** Record an open. Returns null when nothing meaningful changed (same day, < 1 h since last). */
export function recordOpen(prefs: AdminNavPrefs, cardId: string, nowMs: number): AdminNavPrefs | null {
  const today = Math.floor(nowMs / DAY_MS);
  const prev = prefs.usage?.[cardId];
  if (prev && prev.days.includes(today) && nowMs - prev.last < 60 * 60 * 1000) return null;
  const days = Array.from(new Set([...(prev?.days ?? []), today]))
    .filter((d) => d > today - MOST_USED_WINDOW_DAYS)
    .sort((a, b) => a - b);
  return {
    ...prefs,
    trackingStartedAt: prefs.trackingStartedAt ?? nowMs,
    usage: { ...(prefs.usage ?? {}), [cardId]: { last: nowMs, days } },
    updatedAt: nowMs,
  };
}

export function isRarelyUsed(cardId: string, prefs: AdminNavPrefs, nowMs: number): boolean {
  if (prefs.pinned?.includes(cardId)) return false;
  const started = prefs.trackingStartedAt;
  const cutoff = RARELY_USED_AFTER_DAYS * DAY_MS;
  // Grace period: nothing hides until we have RARELY_USED_AFTER_DAYS of real usage data.
  if (!started || nowMs - started < cutoff) return false;
  const last = prefs.usage?.[cardId]?.last;
  return !last || nowMs - last > cutoff;
}

/** Most used: distinct days opened in the last 30 days, then most recent. */
export function mostUsedCards(cards: AdminNavCard[], prefs: AdminNavPrefs, nowMs: number, n: number): AdminNavCard[] {
  const today = Math.floor(nowMs / DAY_MS);
  const score = (id: string) => (prefs.usage?.[id]?.days ?? []).filter((d) => d > today - MOST_USED_WINDOW_DAYS).length;
  return cards
    .filter((c) => score(c.id) > 0)
    .sort((a, b) => score(b.id) - score(a.id) || (prefs.usage?.[b.id]?.last ?? 0) - (prefs.usage?.[a.id]?.last ?? 0))
    .slice(0, n);
}

/** Merge local + server prefs: usage keeps the newest per card; settings from the newer side. */
export function mergeAdminNavPrefs(a: AdminNavPrefs | undefined, b: AdminNavPrefs | undefined): AdminNavPrefs {
  if (!a) return b ?? {};
  if (!b) return a;
  const newer = (a.updatedAt ?? 0) >= (b.updatedAt ?? 0) ? a : b;
  const usage: Record<string, AdminCardUsage> = { ...(b.usage ?? {}) };
  Object.entries(a.usage ?? {}).forEach(([id, u]) => {
    const other = usage[id];
    usage[id] = !other
      ? u
      : { last: Math.max(u.last, other.last), days: Array.from(new Set([...u.days, ...other.days])).sort((x, y) => x - y) };
  });
  const starts = [a.trackingStartedAt, b.trackingStartedAt].filter((v): v is number => typeof v === 'number');
  return {
    ...newer,
    trackingStartedAt: starts.length ? Math.min(...starts) : undefined,
    usage,
    updatedAt: Math.max(a.updatedAt ?? 0, b.updatedAt ?? 0) || undefined,
  };
}

export function matchesCardSearch(card: AdminNavCard, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || card.title.toLowerCase().includes(q) || card.description.toLowerCase().includes(q);
}
